import { useState } from 'react';
import { Field } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { initials } from '../../lib/session';
import { useCustomerDuplicates, useCustomers } from '../../lib/quotes-api';

/** Cliente-Rapido: "Já existe Mariana Souza · (48) 9…" with "Usar esta cliente". */
export function DuplicateWarning({
  name,
  whatsapp,
  onUse,
}: {
  name: string;
  whatsapp: string;
  onUse: (c: { id: string; name: string }) => void;
}) {
  const dups = useCustomerDuplicates(name, whatsapp);
  const found = dups.data?.data ?? [];
  if (!found.length) return null;
  return (
    <div className="col" style={{ gap: 6, gridColumn: '1 / -1' }}>
      {found.map((c) => (
        <div key={c.id} className="alert warn" style={{ padding: '10px 12px' }}>
          <Icon name="users" />
          <div className="grow sm">
            Já existe <b>{c.name}</b>
            {c.whatsapp
              ? ` · ${c.whatsapp}`
              : c.marketplaceNickname
                ? ` · ${c.marketplaceNickname}`
                : ''}
          </div>
          <button type="button" className="btn sm" onClick={() => onUse(c)}>
            Usar esta cliente
          </button>
        </div>
      ))}
    </div>
  );
}

export interface CustomerChoice {
  /** Existing customer… */
  id: string | null;
  /** …or a new one created with the quote. */
  newName: string;
  newWhatsapp: string;
  newNickname: string;
  isNew: boolean;
  /** Display name of the existing one. */
  name: string;
}

export const emptyChoice: CustomerChoice = {
  id: null,
  newName: '',
  newWhatsapp: '',
  newNickname: '',
  isNew: false,
  name: '',
};

/** Customer search with a short list and "+ Novo cliente" inline (board QQ-Desktop save dialog). */
export function CustomerSearch({
  value,
  onChange,
  error,
}: {
  value: CustomerChoice;
  onChange: (c: CustomerChoice) => void;
  error?: string | null;
}) {
  const [q, setQ] = useState('');
  const list = useCustomers(q);
  const rows = (list.data?.data ?? []).slice(0, 5);
  return (
    <div className="field">
      <label htmlFor="qcli">Cliente</label>
      <label className="search" style={{ maxWidth: 'none', flex: 'none' }}>
        <Icon name="search" size={18} />
        <input
          id="qcli"
          placeholder="Buscar por nome ou WhatsApp"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </label>
      {value.isNew ? null : (
        <div className="col" style={{ gap: 6 }}>
          {rows.map((k) => {
            const on = value.id === k.id;
            return (
              <button
                key={k.id}
                type="button"
                className={on ? 'pick on' : 'pick'}
                style={{ minHeight: 44 }}
                aria-pressed={on}
                onClick={() => onChange({ ...emptyChoice, id: k.id, name: k.name })}
              >
                <span
                  className="avatar"
                  style={{ width: 28, height: 28, flexBasis: 28, fontSize: 12 }}
                >
                  {initials(k.name)}
                </span>
                <span className="grow">{k.name}</span>
                <span className="xs muted">
                  {k.whatsapp ?? k.marketplaceNickname ?? k.email ?? ''}
                </span>
              </button>
            );
          })}
          {list.data && rows.length === 0 ? (
            <p className="sm muted">
              {q.trim()
                ? `Ninguém com “${q.trim()}”. Cadastre abaixo.`
                : 'Nenhum cliente ainda. Cadastre abaixo.'}
            </p>
          ) : null}
        </div>
      )}
      <button
        type="button"
        className="btn sm g"
        style={{ alignSelf: 'flex-start', padding: 0 }}
        aria-expanded={value.isNew}
        onClick={() =>
          onChange(
            value.isNew
              ? emptyChoice
              : {
                  ...emptyChoice,
                  isNew: true,
                  newName: q.trim() && !/^\d/.test(q.trim()) ? q.trim() : '',
                },
          )
        }
      >
        {value.isNew ? 'Escolher cliente da lista' : '+ Novo cliente'}
      </button>
      {value.isNew ? (
        <div
          className="card sunk"
          style={{ padding: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}
        >
          <Field id="ncn" label="Nome">
            <input
              id="ncn"
              className="input"
              value={value.newName}
              onChange={(e) => onChange({ ...value, newName: e.target.value })}
            />
          </Field>
          <Field id="ncw" label="WhatsApp">
            <input
              id="ncw"
              className="input"
              inputMode="tel"
              placeholder="(48) 9____-____"
              value={value.newWhatsapp}
              onChange={(e) => onChange({ ...value, newWhatsapp: e.target.value })}
            />
          </Field>
          <Field id="ncm" label="Apelido no marketplace">
            <input
              id="ncm"
              className="input"
              placeholder="opcional"
              value={value.newNickname}
              onChange={(e) => onChange({ ...value, newNickname: e.target.value })}
            />
          </Field>
          <DuplicateWarning
            name={value.newName}
            whatsapp={value.newWhatsapp}
            onUse={(c) => onChange({ ...emptyChoice, id: c.id, name: c.name })}
          />
          <p className="xs muted" style={{ gridColumn: '1 / -1' }}>
            CPF e endereço só quando precisar de nota fiscal ou envio direto.
          </p>
        </div>
      ) : null}
      {error ? (
        <p className="help err">
          <Icon name="alert" size={14} />
          {error}
        </p>
      ) : null}
    </div>
  );
}
